import { useEffect, useRef, RefObject, useLayoutEffect } from 'react';

import { PORTAL_IDS } from 'shared/entities/app';

export const useOnEscape = (
  handler: (event: KeyboardEvent) => void,
  active = true
) => {
  useEffect(() => {
    if (!active) {
      return;
    }
    const listener = (event: KeyboardEvent) => {
      // check if key is an Escape
      if (event.key === 'Escape') {
        handler(event);
      }
    };
    document.addEventListener('keyup', listener);

    return () => {
      if (!active) {
        return;
      }
      document.removeEventListener('keyup', listener);
    };
  }, [handler, active]);
};

export const useRepositionOnResize = (handler: () => void, active = true) => {
  useEffect(() => {
    if (!active) {
      return;
    }
    const listener = () => {
      handler();
    };

    window.addEventListener('resize', listener);

    return () => {
      if (!active) {
        return;
      }
      window.removeEventListener('resize', listener);
    };
  }, [handler, active]);
};

export const useOnClickOutside = (
  ref: RefObject<HTMLElement> | RefObject<HTMLElement>[],
  handler: (event: PointerEvent) => void,
  active = true
) => {
  useEffect(() => {
    if (!active) {
      return;
    }
    const listener = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      const refs = Array.isArray(ref) ? ref : [ref];

      const contains = refs.some(
        (r) => r.current && r.current.contains(target)
      );

      event.stopPropagation();

      if (contains) {
        return;
      }

      const popupRoot = document.getElementById(PORTAL_IDS.POPUPS);
      if (popupRoot?.contains(target)) {
        return;
      }

      handler(event);
    };

    document.addEventListener('pointerdown', listener);

    return () => {
      if (!active) {
        return;
      }
      document.removeEventListener('pointerdown', listener);
    };
  }, [ref, handler, active]);
};

const FOCUSABLE_SELECTOR =
  'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]';

/**
 * Переводит фокус внутрь при открытии и возвращает его назад при закрытии.
 * Парная к useTabbing половина: та удерживает фокус внутри, эта заводит и выводит.
 */
export const useModalFocus = (
  contentRef: RefObject<HTMLElement>,
  opened: boolean,
  initialFocusRef?: RefObject<HTMLElement>
) => {
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (!opened) {
      return;
    }

    previouslyFocusedRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
  }, [opened]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    const focusTarget =
      initialFocusRef?.current ||
      contentRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ||
      contentRef.current;
    focusTarget?.focus();

    return () => {
      const previousElement = previouslyFocusedRef.current;
      if (previousElement?.isConnected) {
        previousElement.focus();
        return;
      }

      const main = document.querySelector<HTMLElement>('main');
      if (!main) {
        return;
      }

      const previousTabIndex = main.getAttribute('tabindex');
      main.tabIndex = -1;
      main.focus();

      if (previousTabIndex === null) {
        main.removeAttribute('tabindex');
      } else {
        main.setAttribute('tabindex', previousTabIndex);
      }
    };
  }, [contentRef, initialFocusRef, opened]);
};

// Make sure that user is not able TAB out of the Modal content on Open
export const useTabbing = (
  contentRef: RefObject<HTMLElement>,
  active = true
) => {
  useEffect(() => {
    if (!active) {
      return;
    }
    const listener = (event: KeyboardEvent) => {
      // check if key is an Tab
      if (event.key === 'Tab') {
        const els = contentRef?.current?.querySelectorAll(FOCUSABLE_SELECTOR);

        const focusableEls = Array.prototype.slice.call(els);
        if (focusableEls.length === 0) {
          event.preventDefault();
          contentRef.current?.focus();
          return;
        }

        if (focusableEls.length === 1) {
          event.preventDefault();
          return;
        }

        const firstFocusableEl = focusableEls[0] as HTMLElement;
        const lastFocusableEl = focusableEls[
          focusableEls.length - 1
        ] as HTMLElement;
        const activeElementIndex = focusableEls.indexOf(document.activeElement);

        if (event.shiftKey && activeElementIndex <= 0) {
          event.preventDefault();
          lastFocusableEl.focus();
        } else if (
          !event.shiftKey &&
          activeElementIndex === focusableEls.length - 1
        ) {
          event.preventDefault();
          firstFocusableEl.focus();
        } else if (!event.shiftKey && activeElementIndex === -1) {
          event.preventDefault();
          firstFocusableEl.focus();
        }
      }
    };

    document.addEventListener('keydown', listener);

    return () => {
      if (!active) {
        return;
      }
      document.removeEventListener('keydown', listener);
    };
  }, [contentRef, active]);
};

export const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;
